/**
 * useModelLoader Hook
 * 
 * Load GLB/GLTF model dengan:
 * - Auto-smooth shading algorithm
 * - Camera detection & helpers
 * - Texture extraction
 * - Auto-center & scale
 * - Animation detection
 * 
 * @param {Object} sceneRef - Scene reference
 * @param {Object} options - Loading options
 * @returns {Object} Model state dan methods
 */

import { useState, useEffect, useRef } from 'react'
import { applyAutoSmoothNormals } from '@utils/autoSmooth'

export function useModelLoader(sceneRef, options = {}) {
  const {
    modelFile,
    modelUrl,
    isReady,
    enableAutoSmooth = true,
    autoSmoothAngle = 30,
    hdriIntensity = 1,
    onCamerasLoaded,
    onAnimationsLoaded,
    onTexturesLoaded,
  } = options

  const modelRef = useRef(null)
  const cameraHelpersRef = useRef([])

  const [cameras, setCameras] = useState([])
  const [selectedCamera, setSelectedCamera] = useState('default')
  const selectedCameraRef = useRef('default')
  
  const [animations, setAnimations] = useState([])
  const [textures, setTextures] = useState([])
  
  const [currentAssetLoading, setCurrentAssetLoading] = useState('')
  const [currentAssetProgress, setCurrentAssetProgress] = useState(0)
  const [loadingStatus, setLoadingStatus] = useState('')
  const [error, setError] = useState(null)

  // Load 3D model
  useEffect(() => {
    const modelSource = modelFile || modelUrl

    if (
      !isReady ||
      !modelSource ||
      !sceneRef.current ||
      !window.THREE_MODULE ||
      !window.GLTFLoader
    ) {
      return
    }

    const THREE = window.THREE_MODULE
    console.log('Loading model from:', modelSource)

    // Remove old model and helpers
    if (modelRef.current) {
      sceneRef.current.remove(modelRef.current)
      modelRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose()
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose())
          } else {
            child.material.dispose()
          }
        }
      })
      modelRef.current = null
    }

    // Remove old camera helpers
    if (cameraHelpersRef.current.length > 0) {
      cameraHelpersRef.current.forEach((helper) => {
        sceneRef.current.remove(helper)
        helper.dispose()
      })
      cameraHelpersRef.current = []
    }

    // Reset loading states
    setCurrentAssetLoading('3D Model')
    setCurrentAssetProgress(0)

    // Use loading manager if available
    const loader = window.LoadingManager
      ? new window.GLTFLoader(window.LoadingManager)
      : new window.GLTFLoader()

    loader.load(
      modelSource,
      (gltf) => {
        console.log('✅ GLB Model loaded successfully')
        console.log('GLTF object:', gltf)
        console.log('gltf.scene:', gltf.scene)
        console.log('gltf.animations:', gltf.animations)
        
        const model = gltf.scene

        // Collect textures
        const textureList = []
        const textureMap = new Map()

        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true
            child.receiveShadow = true

            // Apply Auto Smooth Shading
            if (enableAutoSmooth && child.geometry) {
              applyAutoSmoothNormals(child.geometry, autoSmoothAngle, THREE)
            } else if (child.geometry && !enableAutoSmooth) {
              child.geometry.computeVertexNormals()
            }

            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material]

            materials.forEach((material) => {
              if (material) {
                material.needsUpdate = true

                // Enable environment map
                if (
                  material.isMeshStandardMaterial ||
                  material.isMeshPhysicalMaterial
                ) {
                  material.envMapIntensity = hdriIntensity || 1.0

                  // Reset emissive
                  if (material.emissive) {
                    material.emissive.setHex(0x000000)
                  }
                  if (material.emissiveIntensity !== undefined) {
                    material.emissiveIntensity = 0
                  }
                }

                // Reset emissive for Lambert
                if (material.isMeshLambertMaterial) {
                  if (material.emissive) {
                    material.emissive.setHex(0x000000)
                  }
                }

                // Collect textures
                const textureTypes = [
                  'map',
                  'normalMap',
                  'roughnessMap',
                  'metalnessMap',
                  'aoMap',
                  'emissiveMap',
                ]

                textureTypes.forEach((texType) => {
                  if (material[texType]) {
                    const texture = material[texType]
                    const textureId = texture.uuid

                    texture.center.set(0.5, 0.5)

                    // Set color space
                    if (texType === 'map' || texType === 'emissiveMap') {
                      if (texture.colorSpace !== undefined) {
                        texture.colorSpace = THREE.SRGBColorSpace
                      } else {
                        texture.encoding = THREE.sRGBEncoding
                      }
                    }

                    texture.needsUpdate = true

                    if (!textureMap.has(textureId)) {
                      textureMap.set(textureId, {
                        id: textureId,
                        name: texture.name || `${texType} ${textureMap.size + 1}`,
                        type: texType,
                        texture: texture,
                        material: material,
                        meshName: child.name || 'Unnamed Mesh',
                      })
                    }
                  }
                })
              }
            })
          }
        })

        // Convert map to array
        textureMap.forEach((texData) => textureList.push(texData))
        setTextures(textureList)
        console.log(`Found ${textureList.length} unique texture(s)`)

        if (onTexturesLoaded) onTexturesLoaded(textureList)

        // CRITICAL: Add gltf.scene to sceneRef (contains animations)
        sceneRef.current.add(gltf.scene)
        modelRef.current = gltf.scene // Store gltf.scene, NOT model!
        
        console.log('✅ gltf.scene added to Three.js scene')
        console.log('modelRef.current:', modelRef.current)

        // Get bounding box untuk auto-framing
        const box = new THREE.Box3().setFromObject(model)
        const center = box.getCenter(new THREE.Vector3())
        const size = box.getSize(new THREE.Vector3())
        const maxDim = Math.max(size.x, size.y, size.z)

        // AUTO-FRAME: Adjust camera untuk lihat model
        console.log('📷 Auto-framing model to camera...')
        console.log('Model size:', size)
        console.log('Model center:', center)
        console.log('Max dimension:', maxDim)

        // Frame model in camera view (if camera & controls available)
        if (window.MAIN_CAMERA && window.ORBIT_CONTROLS) {
          const camera = window.MAIN_CAMERA
          const controls = window.ORBIT_CONTROLS

          // Calculate camera distance untuk fit model
          const fov = camera.fov * (Math.PI / 180)
          let cameraDistance = maxDim / 2 / Math.tan(fov / 2)
          cameraDistance *= 2.5 // Padding agar model tidak terlalu dekat

          // Position camera
          const direction = new THREE.Vector3(1, 1, 1).normalize()
          camera.position.copy(center).add(direction.multiplyScalar(cameraDistance))

          // Update controls target ke center model
          controls.target.copy(center)
          controls.update()

          console.log('✅ Camera positioned at:', camera.position)
          console.log('✅ Camera looking at:', controls.target)
        } else {
          console.warn('⚠️ Camera/controls not available for auto-framing')
          console.log('💡 Model loaded at:', model.position)
          console.log('💡 Try manually zoom/pan to see model')
        }

        // Detect cameras in GLB
        console.log('🎥 ═══════════════════════════════════════')
        console.log('🎥 DETECTING CAMERAS IN GLB...')
        console.log('🎥 ═══════════════════════════════════════')
        
        const sceneCameras = []
        const cameraHelpers = []

        gltf.scene.traverse((child) => {
          if (child.isCamera) {
            sceneCameras.push({
              name: child.name || `Camera ${sceneCameras.length + 1}`,
              camera: child,
              initialPosition: child.position.clone(),
              initialRotation: child.rotation.clone(),
              initialQuaternion: child.quaternion.clone(),
            })

            const helper = new THREE.CameraHelper(child)
            helper.visible = true
            sceneRef.current.add(helper)
            cameraHelpers.push(helper)

            console.log(`📷 Camera found: ${child.name || 'Unnamed'}`, {
              position: child.position.toArray(),
              rotation: child.rotation.toArray(),
              fov: child.fov,
            })
          }
        })

        if (sceneCameras.length > 0) {
          console.log(`✅ Found ${sceneCameras.length} camera(s) in model`)
          console.log('Camera names:', sceneCameras.map(c => c.name))
          setCameras(sceneCameras)
          model.userData.cameras = sceneCameras
          cameraHelpersRef.current = cameraHelpers

          // Auto-select first camera
          setSelectedCamera(sceneCameras[0].name)
          selectedCameraRef.current = sceneCameras[0].name
          console.log('🎥 Auto-selected camera:', sceneCameras[0].name)

          if (onCamerasLoaded) onCamerasLoaded(sceneCameras)
        } else {
          console.log('⚠️ No cameras found in this GLB file')
          console.log('💡 Using default orbit camera')
          setCameras([])
          setSelectedCamera('default')
          selectedCameraRef.current = 'default'
          cameraHelpersRef.current = []

          // Center & scale model if no cameras
          if (maxDim > 0) {
            const scale = 3 / maxDim
            model.scale.multiplyScalar(scale)
            box.setFromObject(model)
            const centeredPosition = box.getCenter(new THREE.Vector3())
            model.position.sub(centeredPosition)
          }
        }
        
        console.log('🎥 ═══════════════════════════════════════')

        // Handle animations - CRITICAL: Create mixer and auto-select!
        if (gltf.animations && gltf.animations.length > 0) {
          // CRITICAL: Create AnimationMixer with gltf.scene (NOT model!)
          // This is needed for camera animations and proper bone animations
          const THREE = window.THREE_MODULE
          
          // Always re-create mixer untuk model baru
          window.ANIMATION_MIXER = new THREE.AnimationMixer(gltf.scene)
          console.log('✅ Animation mixer created with gltf.scene')
          console.log('Mixer target:', window.ANIMATION_MIXER._root)

          const animNames = gltf.animations.map(
            (anim, idx) => anim.name || `Animation ${idx + 1}`
          )
          setAnimations(animNames)
          gltf.scene.userData.animations = gltf.animations // Store di gltf.scene!

          console.log('🎬 ═══════════════════════════════════════')
          console.log(`✅ ANIMATIONS DETECTED: ${gltf.animations.length}`)
          console.log('🎬 ═══════════════════════════════════════')
          gltf.animations.forEach((clip, idx) => {
            console.log(`  [${idx + 1}] "${clip.name || 'Unnamed'}"`)
            console.log(`      Duration: ${clip.duration.toFixed(2)}s`)
            console.log(`      Tracks: ${clip.tracks.length}`)
          })
          console.log('🎬 ═══════════════════════════════════════')
          console.log('Animation names array:', animNames)
          console.log('First animation will be auto-selected:', animNames[0])
          
          if (onAnimationsLoaded) onAnimationsLoaded(animNames, gltf.animations)
        } else {
          console.log('⚠️ No animations found in this model')
          setAnimations([])
        }
      },
      (progress) => {
        if (progress.total > 0) {
          const percent = (progress.loaded / progress.total) * 100
          const loadedMB = (progress.loaded / (1024 * 1024)).toFixed(2)
          const totalMB = (progress.total / (1024 * 1024)).toFixed(2)

          console.log(
            `Loading model: ${percent.toFixed(0)}% (${loadedMB}MB / ${totalMB}MB)`
          )
          setCurrentAssetLoading('3D Model')
          setCurrentAssetProgress(Math.round(percent))
          setLoadingStatus(`Loading 3D Model: ${Math.round(percent)}%`)
        }
      },
      (error) => {
        console.error('Error loading model:', error)
        setError('Failed to load model. Check console for details.')
        setLoadingStatus('Failed to load model')
      }
    )
  }, [isReady, modelFile, modelUrl, enableAutoSmooth, autoSmoothAngle])

  return {
    modelRef,
    model: modelRef.current,
    
    cameras,
    selectedCamera,
    setSelectedCamera,
    selectedCameraRef,
    cameraHelpersRef,
    
    animations,
    textures,
    
    currentAssetLoading,
    currentAssetProgress,
    loadingStatus,
    error,
  }
}

